import get from 'lodash.get';
import pick from 'lodash.pick';
import SV from 'server-vars';
import { graphql, readInlineData } from 'relay-runtime';
import registrationValidator from 'dibs-registration-validator';
import { validate as addressValidator } from 'dibs-address-validator/exports/validate';
import creditCardValidator from 'dibs-credit-card-validator';
import { getExpiryYear, getExpiryMonth } from 'dibs-credit-card-validator';
import {
    getPasswordErrorCodes,
    hasPasswordError,
} from 'dibs-buyer-layout/exports/validatePassword';
import { ERRORS } from '../constants/checkoutConstants';

export const registrationFields = ['email', 'password'];
export const regionFields = ['country', 'city', 'state', 'zipCode'];
export const shippingFields = [
    'firstName',
    'lastName',
    'company',
    'line1',
    'line2',
    'city',
    'state',
    'zipCode',
    'country',
    'phoneNumber',
];
export const billingFields = [
    'firstName',
    'lastName',
    'line1',
    'line2',
    'city',
    'state',
    'zipCode',
    'country',
];
export const creditCardFields = [
    'cardHolderName',
    'creditCardNumber',
    'securityCode',
    'cardExpiry',
];
export const ACHFields = [
    'accountType',
    'ownershipType',
    'routingNumber',
    'accountNumber',
    'firstNameOnAccount',
    'lastNameOnAccount',
    'businessNameOnAccount',
];
export const verifyCardFields = ['creditCardNumber', 'securityCode'];

export function getDefaultCountry() {
    const defaultCountry = SV.get('defaultCountry');
    return typeof defaultCountry === 'string' ? defaultCountry : 'US';
}

export function hasSameFields(fields, obj1 = {}, obj2 = {}) {
    return !fields.some(key => {
        const field1 = (obj1[key] && obj1[key].trim()) || obj1[key];
        const field2 = (obj2[key] && obj2[key].trim()) || obj2[key];
        return field1 !== field2;
    });
}

export function isSameAddresses(address1, address2) {
    return hasSameFields(shippingFields, address1, address2);
}

export function isSameRegion(address1, address2) {
    return hasSameFields(regionFields, address1, address2);
}

export function getTokenizeCardFormat({ cardData = {}, isVerifying }) {
    const billingAddress = cardData.billingAddress;
    const creditCardNumber = cardData.creditCardNumber || '';
    const number = creditCardNumber.replace(/ /g, '');
    const tokenizeCard = {
        number,
        cardholderName: get(cardData, 'cardHolderName'),
        expirationMonth: getExpiryMonth(get(cardData, 'cardExpiry')),
        expirationYear: getExpiryYear(get(cardData, 'cardExpiry')),
        billingAddress: {
            firstName: get(billingAddress, 'firstName') || undefined,
            lastName: get(billingAddress, 'lastName') || undefined,
            company: get(billingAddress, 'company') || undefined,
            streetAddress: get(billingAddress, 'line1'),
            extendedAddress: get(billingAddress, 'line2') || undefined,
            locality: get(billingAddress, 'city'),
            region: get(billingAddress, 'state') || '',
            postalCode: get(billingAddress, 'zipCode'),
            countryCodeAlpha2: get(billingAddress, 'country'),
        },
    };
    if (!isVerifying) {
        tokenizeCard.cvv = cardData.securityCode;
    }
    return tokenizeCard;
}

export function getRegistrationErrors({ form, options }) {
    const registrationValidation = registrationValidator(form, options);
    const errorCodes = getPasswordErrorCodes(form?.password || '');
    registrationValidation.errors.password = errorCodes;
    registrationValidation.isValid =
        registrationValidation.isValid && !hasPasswordError(errorCodes);

    return registrationValidation;
}

export function getEmailOnlyErrors(form) {
    return registrationValidator(form, { fields: ['email'] });
}

export function getAddressErrors(address, isBilling) {
    return addressValidator(
        address,
        isBilling
            ? {
                  allowPOBox: true,
                  isPhoneRequired: false,
              }
            : {}
    );
}

export function getCreditCardErrors(creditCard, isVerifying) {
    const fields = isVerifying ? verifyCardFields : creditCardFields;
    const errors = pick(creditCardValidator(creditCard).errors, fields);
    const isEmpty = !Object.keys(errors).length;
    return { isValid: isEmpty, errors };
}

export function getErrorMessages(errors = {}) {
    return Object.keys(errors).reduce(
        (o, f) => ({
            ...o,
            [f]: get(errors[f], 'defaultMessage') || errors[f] || null,
        }),
        {}
    );
}

export function wasPaymentDenied({ errorStatus, errorMessage }) {
    const deniedMessages = ['DENIED_DATA_VALIDATION', 'FRAUDULENT', 'DENIED_PROCESSOR_ERROR'];
    const is412Error = errorStatus === ERRORS.ERROR_412;
    return is412Error && deniedMessages.includes(errorMessage);
}

export const wasPaymentRemoved = ({ errorStatus }) => {
    return errorStatus === ERRORS.PAYMENT_METHOD_ERROR;
};

/**
 * @param {Object} creditCard
 * @param {string=} creditCard.cardExpiryMonth
 * @param {string=} creditCard.cardExpiryYear
 * @returns {string}
 */
export function formCreditCardExpiryValue(creditCard = {}) {
    const { cardExpiryMonth, cardExpiryYear } = creditCard;

    if (cardExpiryMonth && cardExpiryYear) {
        return `${cardExpiryMonth}/${cardExpiryYear.slice(2)}`;
    }

    return '';
}

export function getTrimmedAddressFields({ address, fields = regionFields }) {
    return fields.reduce((acc, curr) => {
        acc[curr] = address[curr]?.trim();
        return acc;
    }, {});
}

export function getBillingAddressFields(addressRef) {
    if (!addressRef) {
        return {};
    }

    const address = readInlineData(
        graphql`
            fragment checkoutForms_address on Address {
                firstName
                lastName
                country
                line1
                line2
                city
                state
                zipCode
            }
        `,
        addressRef
    );

    return {
        firstName: address?.firstName,
        lastName: address?.lastName,
        line1: address?.line1,
        line2: address?.line2,
        city: address?.city,
        state: address?.state,
        zipCode: address?.zipCode,
        country: address?.country,
    };
}
